/**
 * File downloads inside the Android / iOS app.
 *
 * A WebView has no download manager: `<a download>` / blob links (jsPDF `save`, FileSaver, ExcelJS
 * and CSV exports) and `window.open(blobUrl)` silently do nothing. In the native app those are
 * caught here, written to the app's cache folder and handed to the system share sheet — from
 * which the user saves to Files / Downloads, opens it in a PDF viewer, or sends it.
 *
 * Both entry points FileSaver uses are covered: `a.click()` and `a.dispatchEvent(new MouseEvent('click'))`
 * on a detached anchor (which never reaches a document-level listener).
 */
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const EXT_BY_TYPE = {
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/zip': 'zip',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'text/plain': 'txt',
  'application/json': 'json',
};

const isFileUrl = (href) => /^(blob:|data:)/i.test(String(href || ''));

/** A download link we should take over: an explicit `download` attribute or an in-memory file URL. */
const isDownloadAnchor = (a) => !!a && a.tagName === 'A' && !!a.href && (a.hasAttribute('download') || isFileUrl(a.href));

function safeFileName(name, blobType) {
  const ext = EXT_BY_TYPE[String(blobType || '').split(';')[0]] || '';
  let base = String(name || '').split(/[\\/]/).pop().replace(/[^\w.\- ()]+/g, '_').trim();
  if (!base) base = `download-${Date.now()}`;
  if (ext && !/\.[a-z0-9]{2,5}$/i.test(base)) base = `${base}.${ext}`;
  return base.slice(0, 120);
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

let busy = false;

/** Write the file to the app cache and open the native share sheet for it. */
export async function shareFile(href, name) {
  if (busy) return;
  busy = true;
  try {
    const blob = await (await fetch(href)).blob();
    const path = safeFileName(name, blob.type);
    await Filesystem.writeFile({ path, data: await blobToBase64(blob), directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    await Share.share({ title: path, url: uri, dialogTitle: path });
  } catch (err) {
    // Dismissing the share sheet rejects too — only log real failures.
    if (!/cancel/i.test(String(err?.message || err))) console.warn('[native] download failed', err);
  } finally {
    busy = false;
  }
}

let installed = false;

export function installNativeDownloads() {
  if (installed) return;
  installed = true;

  const take = (anchor) => {
    shareFile(anchor.href, anchor.getAttribute('download') || anchor.download);
  };

  // Exports often revoke the blob URL right after `a.click()`; the share flow reads it
  // asynchronously, so revocation waits a minute in the app (memory is reclaimed then).
  const originalRevoke = URL.revokeObjectURL.bind(URL);
  URL.revokeObjectURL = (url) => {
    setTimeout(() => originalRevoke(url), 60 * 1000);
  };

  const originalClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function click(...args) {
    if (isDownloadAnchor(this)) return take(this);
    return originalClick.apply(this, args);
  };

  const originalDispatch = HTMLAnchorElement.prototype.dispatchEvent;
  HTMLAnchorElement.prototype.dispatchEvent = function dispatchEvent(event) {
    if (event?.type === 'click' && isDownloadAnchor(this)) {
      take(this);
      return false;
    }
    return originalDispatch.call(this, event);
  };

  // Real taps on download links that are in the page.
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target?.closest?.('a');
      if (!isDownloadAnchor(anchor)) return;
      event.preventDefault();
      take(anchor);
    },
    true
  );

  // `window.open(blobUrl)` — PDF previews opened "in a new tab".
  const originalOpen = window.open;
  window.open = function open(url, ...rest) {
    if (isFileUrl(url)) {
      shareFile(url, '');
      return null;
    }
    return originalOpen.call(window, url, ...rest);
  };
}
